// OwnerMatchPrompt — the existing-owner banner / picker under an owner's email on
// the EPMA initiate form (owner-sync spec §3).
//
//   • linked   → "Existing AppFolio owner: Jane Doe (…)" + Not this owner
//   • single   → "Looks like an existing owner: Jane Doe (jane@…, Walla Walla)"
//                + Use this owner / No, new owner
//   • multiple → a list of the matches, each with Use this owner, + New owner
//   • none     → nothing at all (also what a switched-off lookup yields)
//
// Names, emails and cities only — the AppFolio owner id is a React key and a
// request field, never text (docs/no-ids-in-ui.md). Presentational: every
// decision is lib/ownerSync.ts's, so this renders under a bare
// renderToStaticMarkup in tests.

import {
  ownerMatchDetail,
  type OwnerMatch,
  type OwnerMatchPrompt as Prompt,
} from '@/lib/ownerSync';

function MatchName({ m }: { m: OwnerMatch }): React.JSX.Element {
  const detail = ownerMatchDetail(m);
  return (
    <>
      <strong>{m.displayName}</strong>
      {detail ? ` (${detail})` : ''}
    </>
  );
}

export function OwnerMatchPrompt({
  prompt,
  linked,
  onUse,
  onDismiss,
  onUnlink,
}: {
  prompt: Prompt;
  /** The match this row is already linked to, if any. */
  linked?: OwnerMatch | null | undefined;
  onUse: (m: OwnerMatch) => void;
  onDismiss: () => void;
  onUnlink: () => void;
}): React.JSX.Element | null {
  if (linked) {
    return (
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}
        data-testid="owner-match-linked"
      >
        <span className="pill" data-tone="success" data-size="sm">
          Existing AppFolio owner
        </span>
        <span style={{ fontSize: 'var(--text-sm)' }}>
          <MatchName m={linked} />
        </span>
        <button type="button" className="btn" data-variant="ghost" data-size="sm" onClick={onUnlink}>
          Not this owner
        </button>
      </div>
    );
  }

  if (prompt.kind === 'single') {
    const m = prompt.match;
    return (
      <div className="alert" data-tone="info" role="note" data-testid="owner-match-single">
        <div className="alert-body">
          <div>
            Looks like an existing owner: <MatchName m={m} />
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)', flexWrap: 'wrap' }}>
            <button type="button" className="btn" data-size="sm" onClick={() => onUse(m)}>
              Use this owner
            </button>
            <button type="button" className="btn" data-variant="secondary" data-size="sm" onClick={onDismiss}>
              No, new owner
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (prompt.kind === 'multiple') {
    return (
      <div className="alert" data-tone="info" role="note" data-testid="owner-match-multiple">
        <div className="alert-body">
          <div>
            This email matches {prompt.matches.length} existing AppFolio owners. Pick the right one,
            or keep what you typed as a new owner.
          </div>
          <ul
            style={{
              listStyle: 'none',
              margin: 'var(--space-2) 0 0',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
            }}
          >
            {prompt.matches.map((m) => (
              <li
                key={m.appfolioOwnerId}
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}
              >
                <span style={{ flex: '1 1 auto' }}>
                  <MatchName m={m} />
                </span>
                <button type="button" className="btn" data-size="sm" onClick={() => onUse(m)}>
                  Use this owner
                </button>
              </li>
            ))}
            <li>
              <button type="button" className="btn" data-variant="secondary" data-size="sm" onClick={onDismiss}>
                New owner
              </button>
            </li>
          </ul>
        </div>
      </div>
    );
  }

  return null;
}
